import { ReactNode } from 'react';
import { Trans } from 'react-i18next';

import { actions, BUG_REPORT_SEVERITIES } from 'entities';

import { BugIcon, SendIcon, WarningIcon } from 'components/Icons';

import {
  Alert,
  Box,
  Button,
  Column,
  Dot,
  LabeledInput,
  LabeledSelect,
  ResponsiveOverlay,
  Row,
} from 'components/Blocks';
import { FileIconInput } from 'components/Uploads';
import { TextareaWithFiles } from 'components/Inputs';

import { SVGAnimation } from 'components/SVGAnimation';
import success from 'assets/animations/success.json';

import { BugLinkInput } from './LinkInput';

import { useFileInput, useFormControlV2 } from 'hooks';
import { useLang } from 'hooks/lang';

const TEXT_MIN_LENGTH = 10;
const TITLE_MIN_LENGTH = 5;

interface BugReportModalProps {
  trigger: ReactNode;
}

export function CreateBugReportModal({ trigger }: BugReportModalProps) {
  const { t } = useLang();

  const fileInput = useFileInput();

  const [{ description, title, sent, severity, link }, { handleChange, resetDefault, dispatch }] =
    useFormControlV2({
      description: '',
      title: '',
      sent: false,
      severity: 'NOT_URGENT' as const,
      link: '',
    });

  const hasFiles = !!fileInput.files.length;

  const isDisabled = [
    description.length <= TEXT_MIN_LENGTH,
    title.length <= TITLE_MIN_LENGTH,
    !hasFiles,
  ].some(Boolean);

  const sendBug = actions.feedback.createBugReport.use({
    block: isDisabled,

    onSuccess: () => {
      dispatch({ sent: true });
      fileInput.clearFiles();
    },

    params: {
      files: fileInput.files.map((f) => f.file),
      description,
      title,
      severity,
      link: link || undefined,
    },
  });

  return (
    <ResponsiveOverlay largeClassName="min-w-[520px]" className="overflow-auto" trigger={trigger}>
      {sent ? (
        <Column alignItems="center" gap="2">
          <SVGAnimation animationData={success} size={120} />

          <h2 className="text-success">{t('success')}!</h2>

          <p>{t('report-received')}</p>

          <p>{t('report-context')}</p>

          <Button variant="ghost" onClick={resetDefault}>
            <BugIcon />
            {t('send-another-one')}
          </Button>
        </Column>
      ) : (
        <>
          <Box className="mx-auto size-16 rounded-full border-primary bg-primary/20 p-2">
            <BugIcon size={36} color="primary-light" />
          </Box>

          <Column gap="1" alignItems="center">
            <h2 className="mx-auto text-center text-3xl text-primary">{t('whats-happening?')}</h2>

            <p className="text-center text-muted-foreground/50">{t('report-any-feature')}</p>
          </Column>

          <Column gap="6">
            <LabeledInput
              label={t('title')}
              value={title}
              name="title"
              onChange={handleChange}
              placeholder={t('max-65-charts-title')!}
              className="placeholder:text-xs placeholder:italic"
            />

            <LabeledSelect
              label={t('urgency')}
              value={severity}
              onChange={handleChange}
              name="severity"
              isSearchable={false}
              labelIcon={WarningIcon}
              options={BUG_REPORT_SEVERITIES.map((config) => ({
                ...config,

                label: (
                  <Row gap="2">
                    <Dot color={config.color} />
                    <span>{config.getLabel(t)}</span>
                  </Row>
                ),
              }))}
            />

            <TextareaWithFiles
              className="min-h-24"
              label={t('describe-the-issue')}
              name="description"
              onChange={handleChange}
              placeholder={`${t('detailed-explanation-here')}...`}
              {...fileInput}
              getFileInput={({ onFileInput }) => (
                <>
                  {!hasFiles && (
                    <span className="ml-auto text-xs text-destructive">{t('add-a-file')}!</span>
                  )}

                  <FileIconInput
                    onChange={onFileInput}
                    className={hasFiles ? 'ml-auto' : 'text-destructive'}
                  />
                </>
              )}
            />

            <BugLinkInput value={link} setLink={(link) => dispatch({ link })} />

            <Alert title={`${t('attention')}!`}>
              <Column gap="1">
                <Row gap="2">
                  <Dot color="primary" size={4} />
                  <span>
                    <Trans i18nKey="provide-detailed-explanation" />
                  </span>
                </Row>

                <Row gap="2">
                  <Dot color="primary" size={4} />
                  <span>{t('add-min-1-file')}</span>
                </Row>

                <Row gap="2">
                  <Dot color="primary" size={4} />
                  <span>
                    <Trans i18nKey="pick-correct-urgency" />
                  </span>
                </Row>
              </Column>
            </Alert>

            <Button variant="outlined" {...sendBug.buttonProps} disabled={isDisabled}>
              <SendIcon />
              {t('send')}
            </Button>
          </Column>
        </>
      )}
    </ResponsiveOverlay>
  );
}
